import jsPDF from "jspdf";
import { OUTFIT_REGULAR, OUTFIT_BOLD } from "./outfitFont";
import { FAUTRAS_LOGO } from "./logo";
import { t as tr, localizeSalon } from "./i18n";

const fmt = (n, d = 0) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d }).replace(/[\u202f\u00a0]/g, " ");
const eur = (n) => `${fmt(Math.round(Number(n || 0)))} €`;
const GOLD = [158, 123, 65], INK = [20, 20, 20];

const asset = async (path) => {
  try {
    const r = await fetch(path);
    if (!r.ok) return null;
    const b = await r.blob();
    return await new Promise((res) => { const f = new FileReader(); f.onload = () => res(f.result); f.readAsDataURL(b); });
  } catch (e) { return null; }
};

const strike = (doc, text, x, y, align = "left", color = GOLD) => {
  const w = doc.getTextWidth(text);
  const x0 = align === "right" ? x - w : x;
  const fs = doc.getFontSize();
  doc.setDrawColor(...color); doc.setLineWidth(Math.max(0.8, fs / 14));
  doc.line(x0 - 2, y - fs * 0.05, x0 + w + 2, y - fs * 0.6);
};

const STANDARD_EQUIP = {
  van: ["Structure garantie 8 ans (avec arceau de sécurité)", "Roue de secours", "Plancher imputrescible garantie à vie", "Antivol intégré", "Carrosserie entièrement polyester"],
  prosport: ["Structure garantie 8 ans (avec arceau de sécurité)", "Roue de secours", "Antivol intégré", "Carrosserie entièrement polyester monocoque"],
};

// Standard equipment: range defaults + admin details (one per line) or model name parentheses split on "+" / ","
export const equipmentOf = (p) => {
  const src = (p.model_details || "").trim()
    ? p.model_details.split(/\n+/)
    : [...String(p.model_name || "").matchAll(/\(([^)]*)\)/g)].flatMap((m) => m[1].split(/\s*[+,]\s*/));
  const own = src.map((s) => s.replace(/^[-•\s]+/, "").trim()).filter(Boolean).map((s) => s[0].toUpperCase() + s.slice(1));
  const base = (p.standard_equipment || []).length ? p.standard_equipment : (STANDARD_EQUIP[p.range_key || "van"] || []);
  const all = [...base, ...own];
  return all.filter((s, i) => all.findIndex((x) => x.toLowerCase() === s.toLowerCase()) === i);
};

const heading = (doc, text, x, y) => {
  text = tr(text).toUpperCase();
  doc.setFont("Outfit", "bold"); doc.setFontSize(15); doc.setTextColor(...INK);
  doc.text(text, x, y);
  doc.setDrawColor(...INK); doc.setLineWidth(0.9);
  doc.line(x, y + 2.5, x + doc.getTextWidth(text), y + 2.5);
};

export async function generatePosterPdf(p, { preview = false } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  doc.addFileToVFS("Outfit-Regular.ttf", OUTFIT_REGULAR);
  doc.addFont("Outfit-Regular.ttf", "Outfit", "normal");
  doc.addFileToVFS("Outfit-Bold.ttf", OUTFIT_BOLD);
  doc.addFont("Outfit-Bold.ttf", "Outfit", "bold");
  const W = doc.internal.pageSize.getWidth();
  const [rMark, thumbs] = await Promise.all([asset("/assets/poster/r-watermark.png"), asset("/assets/poster/thumbs.png")]);
  const mx = 33;
  const S = 1.3; // body text scale (titles unchanged)
  const fitText = (txt, x, y, maxW, size, opts) => {
    doc.setFontSize(size);
    const w = doc.getTextWidth(txt);
    if (w > maxW) doc.setFontSize(size * maxW / w);
    doc.text(txt, x, y, opts);
    doc.setFontSize(size);
  };

  if (rMark) doc.addImage(rMark, "PNG", 28, 195, 472, 496);

  // Header: logo + salon (left), model name (right)
  try {
    const lp = doc.getImageProperties(FAUTRAS_LOGO);
    const lh = 54, lw = (lp.width / lp.height) * lh;
    doc.addImage(FAUTRAS_LOGO, "PNG", mx + 6, 48, lw, lh);
  } catch (e) { /* noop */ }
  doc.setFont("Outfit", "bold"); doc.setFontSize(13 * S); doc.setTextColor(70, 70, 70);
  doc.text(doc.splitTextToSize(localizeSalon(p.salon), 170), mx + 6, 124);

  const name = (p.model_name_short || p.model_name || "").toUpperCase().trim();
  const tx = 290;
  doc.setTextColor(...GOLD);
  doc.setFont("Outfit", "bold");
  const [mainName, ...le] = name.split(/\s+(?=LIMITED EDITION)/);
  let ts = 36;
  let tl;
  for (; ts >= 18; ts -= 1) {
    doc.setFontSize(ts);
    tl = doc.splitTextToSize(mainName, W - mx - tx);
    if (tl.length <= (le.length ? 1 : 2)) break;
  }
  doc.text(tl, tx, 90, { lineHeightFactor: 1.05 });
  if (le.length) { doc.setFontSize(20); doc.text(le.join(" "), tx, 90 + ts * 1.05 * tl.length - ts * 0.25); }
  if (p.ptac && p.range_key !== "occasion") {
    doc.setFont("Outfit", "bold"); doc.setFontSize(11 * S); doc.setTextColor(...INK);
    doc.text(`F2 : ${p.ptac} kg${p.show_empty_weight && p.empty_weight ? `  ·  ${tr("Poids à vide")} : ${p.empty_weight} kg` : ""}`, tx, Math.min(178, 90 + ts * 1.05 * tl.length + (le.length ? 14 : 4)));
  }

  // Standard equipment (2 columns)
  let y = 185;
  const COLOR_RE = /^(blanc|blanche|gris|grise|noir|noire|bleu|bleue|rouge|vert|verte|beige|argent|argenté|marron|bordeaux|jaune|orange|ivoire|crème|anthracite|champagne|bronze|doré|taupe|sable)\b/i;
  const allEquip = p.range_key === "occasion" ? [] : equipmentOf(p);
  const equipColors = allEquip.filter((e) => COLOR_RE.test(e));
  const colors = (equipColors.length ? equipColors : (p.available_colors?.length ? p.available_colors : (p.color ? [p.color] : []))).map((c) => tr(c));
  const f2 = (p.ptac_range || []).filter(Boolean);
  const f2Line = f2.length ? [`F2 : ${f2[0] === f2[f2.length - 1] ? f2[0] : `${f2[0]} ${tr("à")} ${f2[f2.length - 1]}`} kg`] : [];
  const equip = [...allEquip.filter((e) => !COLOR_RE.test(e)).map((e) => tr(e)), ...(p.range_key === "occasion" ? [] : f2Line)];
  if (p.range_key === "occasion") {
    doc.setFont("Outfit", "bold"); doc.setFontSize(13 * S); doc.setTextColor(...GOLD);
    doc.text(tr("OCCASION").toUpperCase(), tx, 90 + ts * 1.05 * tl.length + 4);
    doc.setFont("Outfit", "normal"); doc.setFontSize(10.5 * S); doc.setTextColor(...INK);
    doc.text(doc.splitTextToSize((p.used_line || "").replace(new RegExp(`^(Occasion|${tr("Occasion").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}) · `), ""), W - mx - tx), tx, 90 + ts * 1.05 * tl.length + 22);
    if (p.warranty_line) { doc.setFont("Outfit", "bold"); doc.setTextColor(...GOLD); doc.text(doc.splitTextToSize(p.warranty_line, W - mx - tx), tx, 90 + ts * 1.05 * tl.length + 54); }
    const items = String(p.model_details || "").split(/\n+/).map((s) => s.replace(/^[-•\s]+/, "").trim()).filter(Boolean);
    y = Math.max(y, 90 + ts * 1.05 * tl.length + (p.warranty_line ? 84 : 56));
    if (items.length) {
      heading(doc, "OPTIONS ET EQUIPEMENTS", mx, y);
      y += 26;
      doc.setFont("Outfit", "normal"); doc.setTextColor(...INK); doc.setDrawColor(...INK); doc.setLineWidth(0.7);
      const half = Math.ceil(items.length / 2), rowH = Math.min(13.5 * S, (600 - y) / half);
      items.forEach((e, i) => {
        const x = i < half ? mx + 3 : 310, yy = y + (i % half) * rowH;
        doc.rect(x, yy - 7.5, 7.5, 7.5);
        fitText(e, x + 16, yy, (i < half ? 310 : W - mx) - x - 22, 10.5 * S);
      });
    }
  }
  if (equip.length) {
    heading(doc, "EQUIPEMENTS DE SERIE", mx, y);
    y += 26;
    const half = Math.ceil(equip.length / 2);
    const rowH = 13.5 * S;
    doc.setFont("Outfit", "normal"); doc.setTextColor(...INK);
    doc.setDrawColor(...INK); doc.setLineWidth(0.7);
    equip.forEach((e, i) => {
      const x = i < half ? mx + 3 : 310;
      const yy = y + (i % half) * rowH;
      doc.rect(x, yy - 7.5, 7.5, 7.5);
      fitText(e, x + 16, yy, (i < half ? 310 : W - mx) - x - 22, 10.5 * S);
    });
    y += half * rowH + (colors.length ? 20 : 36);
  }
  if (colors.length) {
    if (!equip.length) y += 10;
    y -= 6;
    doc.setFontSize(10.5 * S); doc.setTextColor(...INK);
    doc.setFont("Outfit", "bold");
    const lab = `${equipColors.length || p.available_colors?.length ? tr("Couleurs disponibles") : tr("Couleur")} : `;
    doc.text(lab, mx + 3, y);
    const lw = doc.getTextWidth(lab);
    doc.setFont("Outfit", "normal");
    const lines = doc.splitTextToSize(colors.join(", "), W - mx - (mx + 3 + lw));
    doc.text(lines, mx + 3 + lw, y);
    y += lines.length * 10.5 * S * 1.15 + 34;
  }

  // Options of the displayed model
  const opts = p.options || [];
  const priceTop = 640;
  if (opts.length) {
    heading(doc, "OPTIONS DU MODELE EXPOSE", mx, y);
    y += 28;
    const lh = Math.max(14, Math.min(22 * S, (priceTop - 34 - y) / opts.length));
    const fs = Math.min(11.5 * S, lh * 0.62);
    opts.forEach((o) => {
      const disc = Math.round(o.cat_ttc) > Math.round(o.salon_ttc);
      doc.setFont("Outfit", "normal"); doc.setTextColor(...INK);
      fitText(`${tr(o.name)}${o.qty > 1 ? ` (x${o.qty})` : ""}`, mx + 20, y, 335, fs);
      const ct = eur(o.cat_ttc);
      doc.text(ct, 448, y, { align: "right" });
      if (disc) {
        strike(doc, ct, 448, y, "right");
        doc.setFont("Outfit", "bold"); doc.setTextColor(...GOLD);
        doc.text(Math.round(o.salon_ttc) === 0 ? tr("OFFERT").toUpperCase() : eur(o.salon_ttc), 518, y, { align: "center" });
      }
      y += lh;
    });
  }

  // Price of displayed model
  const vat = Number(p.vat_pct ?? 20) / 100;
  const ttc = Number(p.total_salon_ttc || 0), ht = ttc / (1 + vat);
  const totDisc = Math.round(p.total_cat_ttc) > Math.round(ttc);
  heading(doc, p.range_key === "occasion" ? "PRIX DU VEHICULE" : "PRIX DU MODELE EXPOSE", mx, priceTop);
  if (totDisc) {
    doc.setFont("Outfit", "bold"); doc.setFontSize(28 * S); doc.setTextColor(...INK);
    const ct = eur(p.total_cat_ttc);
    doc.text(ct, W - mx - 14, priceTop, { align: "right" });
    strike(doc, ct, W - mx - 14, priceTop, "right");
  }
  const by = priceTop + 46;
  doc.setFont("Outfit", "normal"); doc.setFontSize(14 * S); doc.setTextColor(...INK);
  const htTxt = `${fmt(ht, 2)} €`, tvaTxt = `${fmt(ttc - ht, 2)} €`;
  doc.text(htTxt, mx + 10, by);
  const htEnd = mx + 10 + doc.getTextWidth(htTxt);
  doc.text(tvaTxt, 215, by);
  const tvaEnd = 215 + doc.getTextWidth(tvaTxt);
  doc.setFontSize(6 * S); doc.text(tr("HT"), htEnd + 4, by); doc.text(vat > 0 ? tr("TVA") : tr("TVA non applicable"), tvaEnd + 4, by);
  doc.setFont("Outfit", "bold"); doc.setFontSize(28 * S); doc.setTextColor(...GOLD);
  doc.setFontSize(6 * S); const ttcW = doc.getTextWidth(tr("TTC")); doc.setFontSize(28 * S);
  const priceRight = Math.min(W - mx - 14, W - 10 - ttcW - 4);
  doc.text(eur(ttc), priceRight, by, { align: "right" });
  doc.setFontSize(6 * S); doc.setTextColor(...INK); doc.text(tr("TTC"), priceRight + 4, by);

  // "A partir de" = model price without options
  const ay = by + 52;
  if (p.range_key !== "occasion") {
  doc.setFont("Outfit", "bold"); doc.setFontSize(28 * S * 0.8); doc.setTextColor(...GOLD);
  const priceTxt = eur(p.model_salon_ttc);
  const pw = doc.getTextWidth(priceTxt);
  doc.text(priceTxt, priceRight, ay, { align: "right" });
  if (Math.round(p.model_cat_ttc) > Math.round(p.model_salon_ttc)) {
    doc.setTextColor(...INK);
    const ct = eur(p.model_cat_ttc), cx = priceRight - pw - 18;
    doc.text(ct, cx, ay, { align: "right" });
    strike(doc, ct, cx, ay, "right");
  }
  heading(doc, "PRIX DU MODELE HORS OPTION", mx, ay);
  doc.setFontSize(6 * S); doc.setTextColor(...INK); doc.text(tr("TTC"), priceRight + 4, ay);
  }

  // Footer band
  const fy = 772;
  doc.setDrawColor(...GOLD); doc.setLineWidth(1.6);
  doc.line(0, fy, W, fy); doc.line(0, fy + 44, W, fy + 44);
  if (thumbs) doc.addImage(thumbs, "PNG", mx, fy + 7, 30, 30);
  doc.setFont("Outfit", "bold"); doc.setFontSize(17); doc.setTextColor(...GOLD);
  const know = tr("Le saviez-vous").replace(/\s*\?\s*$/, "");
  doc.text(know, mx + 36, fy + 28);
  let fx = mx + 36 + doc.getTextWidth(`${know} `);
  doc.setTextColor(...GOLD); doc.text("?", fx, fy + 28);
  fx += doc.getTextWidth("? ") + 10;
  doc.setTextColor(...INK);
  const isProSport = p.range_key ? p.range_key === "prosport" : /pro/i.test(p.range_name || "");
  const buyBack = isProSport ? tr("Nous pouvons racheter votre ancienne remorque.") : tr("Nous pouvons racheter votre ancien van.");
  fitText(buyBack, fx, fy + 28, W - mx + 20 - fx, 11 * S);
  if ((p.phone || "").trim()) {
    const label = `${tr("Appelez-moi au")} `, num = p.phone.trim();
    doc.setFont("Outfit", "bold"); doc.setFontSize(20);
    const lx = (W - doc.getTextWidth(label + num)) / 2;
    doc.setTextColor(...INK); doc.text(label, lx, fy + 62);
    doc.setTextColor(...GOLD); doc.text(num, lx + doc.getTextWidth(label), fy + 62);
  }

  // i18n keys: tr("Structure garantie 8 ans (avec arceau de sécurité)") tr("Roue de secours") tr("Plancher imputrescible garantie à vie") tr("Antivol intégré") tr("à")
  // tr("OPTIONS ET EQUIPEMENTS") tr("EQUIPEMENTS DE SERIE") tr("OPTIONS DU MODELE EXPOSE") tr("PRIX DU VEHICULE") tr("PRIX DU MODELE EXPOSE") tr("PRIX DU MODELE HORS OPTION")
  const safe = (s) => String(s || "").replace(/[^\w-]+/g, "_").slice(0, 40);
  const filename = `Affiche-${safe(p.salon)}-${safe(p.model_name_short || p.model_name)}.pdf`;
  if (preview) return { data: doc.output("arraybuffer"), save: () => doc.save(filename) };
  doc.save(filename);
}
